'use client';

/**
 * The NET30 application on /purchase-order-policies.php.
 *
 * Replaces the HubSpot form that used to sit here (embed rule waived for this
 * page, Matthew's sign-off 2026-08-14) and posts to
 * `/api/credit-terms-application`, which files into the same
 * `credit_terms_requests` queue the sales team already reviews.
 *
 * Client component on purpose: the page is ISR, so nothing session-dependent
 * may be rendered on the server. `useSession` decides whether to prefill the
 * email and whether to ask the status endpoint at all — a guest goes straight
 * to the form, exactly as the legacy embed did.
 *
 * Visually this is the storefront's existing request-panel pattern (see
 * TaxExemptionRequestPanel): white card, navy heading, orange primary action.
 * Consistency is the point — a buyer meets both forms in the same session.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { Building2, CheckCircle2, Clock, FileText, UploadCloud, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { checkUploadSizes, describeUploadError, formatMb, MAX_DOCUMENT_BYTES } from '@/lib/upload-limits';

type SignerKind = 'self' | 'delegate';

interface OrganizationStatus {
  id: number;
  name: string;
  purchaseOrderAllowed: boolean;
}

interface AccountStatus {
  approved: boolean;
  hasPendingRequest: boolean;
  organizations: OrganizationStatus[];
}

interface ApiErrorResponse {
  error?: string;
  details?: { fieldErrors?: Record<string, string[] | undefined> };
}

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  company: 'Company',
  street: 'Street address',
  city: 'City',
  state: 'State',
  postalCode: 'ZIP code',
  signerName: 'Signer name',
  signerEmail: 'Signer email',
  accountsPayableEmail: 'Accounts payable email',
  note: 'Anything else',
};

/**
 * Turn the API's answer into one sentence the applicant can act on. Field-level
 * Zod failures are named rather than summarised: "Check Company, ZIP code" is
 * actionable where "validation error" is not.
 */
function mapApiError(body: ApiErrorResponse, status: number): string {
  if (status === 429) {
    return 'Too many applications from this connection. Please wait a few minutes and try again.';
  }
  const uploadMessage = describeUploadError(body.error, status);
  if (uploadMessage) return uploadMessage;

  switch (body.error) {
    case 'request_already_pending':
      return 'An application for this email is already with our credit department. We will be in touch.';
    case 'validation_error': {
      const fields = Object.entries(body.details?.fieldErrors ?? {})
        .filter(([, messages]) => (messages?.length ?? 0) > 0)
        .map(([field]) => FIELD_LABELS[field] ?? field);
      return fields.length > 0
        ? `Please check: ${fields.join(', ')}.`
        : 'Please check the form and try again.';
    }
    default:
      return 'We could not submit your application. Please try again.';
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="my-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {children}
    </section>
  );
}

function StatusCard({
  icon,
  tone,
  badge,
  children,
}: {
  icon: React.ReactNode;
  tone: 'amber' | 'green';
  badge: string;
  children: React.ReactNode;
}) {
  const toneClasses =
    tone === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
  return (
    <Shell>
      <div className="flex items-start gap-4">
        <span
          className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg ${toneClasses}`}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClasses}`}
          >
            {badge}
          </span>
          <div className="mt-1.5 text-sm text-[var(--scw-gray-body)]">{children}</div>
        </div>
      </div>
    </Shell>
  );
}

const labelClass = 'block text-sm font-medium text-[var(--scw-navy)]';
const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-[var(--scw-gray-body)] shadow-sm placeholder:text-[var(--scw-gray-muted)] focus:border-[var(--scw-navy)] focus:ring-2 focus:ring-[var(--scw-navy)]/20 focus:outline-none';

function Field({
  name,
  label,
  children,
}: {
  name: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={name} className={labelClass}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function CreditTermsApplicationPanel() {
  // The CMS preview app prerenders this page with no SessionProvider in the
  // tree, where `useSession()` returns undefined; destructuring it directly
  // killed the preview's static export. Treat that as "unauthenticated".
  const sessionState = useSession() ?? { data: null, status: 'unauthenticated' as const };
  const { data: session, status: sessionStatus } = sessionState;
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [signerKind, setSignerKind] = useState<SignerKind>('self');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/account/credit-terms');
      setAccountStatus(res.ok ? ((await res.json()) as AccountStatus) : null);
    } catch {
      // A status we could not read must never hide the form: showing it is the
      // safe failure, since the endpoint re-checks everything on submit.
      setAccountStatus(null);
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Guests have no status to fetch — the legacy form accepted them and so
    // does this one, so they go straight to the form.
    if (sessionStatus === 'unauthenticated') {
      setStatusLoaded(true);
      return;
    }
    if (sessionStatus !== 'authenticated') return;
    void loadStatus();
  }, [sessionStatus, loadStatus]);

  function pickFile(list: FileList | null) {
    if (!list) return;
    // Copy out of the live FileList immediately: the input is cleared on the
    // next line so re-picking the same file still fires a change event, and
    // that clear empties this same object.
    const picked = Array.from(list)[0] ?? null;
    if (!picked) return;
    const sizeError = checkUploadSizes([picked]);
    if (sizeError) {
      setError(sizeError);
      return;
    }
    setError(null);
    setFile(picked);
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData(event.currentTarget);
      // The file input is controlled through React state so the chip and the
      // size pre-flight stay in sync; put the chosen file back on the body.
      fd.delete('file');
      if (file) fd.append('file', file);

      const res = await fetch('/api/credit-terms-application', { method: 'POST', body: fd });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as ApiErrorResponse;
        setError(mapApiError(body, res.status));
        return;
      }
      setSubmitted(true);
    } catch {
      setError('We could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── after submitting, and for an application already in the queue ────────
  if (submitted || accountStatus?.hasPendingRequest) {
    return (
      <StatusCard icon={<Clock className="size-5" />} tone="amber" badge="Under review">
        Your application is with our credit department. They respond about your credit approval
        status and credit line within one business day.
      </StatusCard>
    );
  }

  if (sessionStatus === 'loading' || !statusLoaded) {
    return (
      <Shell>
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      </Shell>
    );
  }

  // ── already on terms, personally ─────────────────────────────────────────
  if (accountStatus?.approved) {
    return (
      <StatusCard
        icon={<CheckCircle2 className="size-5" />}
        tone="green"
        badge="Approved for purchase orders"
      >
        Your account is approved for purchase order terms. Choose Purchase Order at checkout. To
        change your credit line, contact your SCW representative.
      </StatusCard>
    );
  }

  // ── already on terms through a company ───────────────────────────────────
  const approvedOrganization = accountStatus?.organizations.find((org) => org.purchaseOrderAllowed);
  if (approvedOrganization) {
    return (
      <StatusCard
        icon={<Building2 className="size-5" />}
        tone="green"
        badge="Approved for purchase orders"
      >
        {approvedOrganization.name} is approved for purchase order terms, and you can buy on that
        account. Choose Purchase Order at checkout and select {approvedOrganization.name}.
      </StatusCard>
    );
  }

  // ── the application form ─────────────────────────────────────────────────
  return (
    <Shell>
      <h3 className="text-base font-semibold text-[var(--scw-navy)]">
        Apply for purchase order terms
      </h3>
      <p className="mt-1 text-sm text-[var(--scw-gray-body)]">
        Our credit department reviews every application and responds within one business day. Apply
        with your work email: terms are extended to a company, not an individual.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-6">
        <fieldset className="space-y-4">
          <legend className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--scw-gray-muted)]">
            Your details
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field name="firstName" label="First name">
              <input id="firstName" name="firstName" required maxLength={100} className={inputClass} />
            </Field>
            <Field name="lastName" label="Last name">
              <input id="lastName" name="lastName" required maxLength={100} className={inputClass} />
            </Field>
            <Field name="email" label="Work email">
              <input
                id="email"
                name="email"
                type="email"
                required
                maxLength={255}
                defaultValue={session?.user?.email ?? ''}
                className={inputClass}
              />
            </Field>
            <Field name="phone" label="Phone">
              <input id="phone" name="phone" type="tel" required maxLength={50} className={inputClass} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--scw-gray-muted)]">
            Company
          </legend>
          <Field name="company" label="Company name">
            <input id="company" name="company" required maxLength={255} className={inputClass} />
          </Field>
          <Field name="street" label="Street address">
            <input id="street" name="street" required maxLength={255} className={inputClass} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field name="city" label="City">
              <input id="city" name="city" required maxLength={120} className={inputClass} />
            </Field>
            <Field name="state" label="State">
              <input id="state" name="state" required maxLength={60} className={inputClass} />
            </Field>
            <Field name="postalCode" label="ZIP code">
              <input id="postalCode" name="postalCode" required maxLength={20} className={inputClass} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className={labelClass}>
            Who is authorized to sign Purchase Order Terms?
          </legend>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-3 text-sm text-[var(--scw-gray-body)] has-[:checked]:border-[var(--scw-navy)] has-[:checked]:bg-[var(--scw-navy)]/5">
            <input
              type="radio"
              name="signerKind"
              value="self"
              checked={signerKind === 'self'}
              onChange={() => setSignerKind('self')}
              className="mt-0.5"
            />
            I am authorized to sign Purchase Order Terms on behalf of my company
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-3 text-sm text-[var(--scw-gray-body)] has-[:checked]:border-[var(--scw-navy)] has-[:checked]:bg-[var(--scw-navy)]/5">
            <input
              type="radio"
              name="signerKind"
              value="delegate"
              checked={signerKind === 'delegate'}
              onChange={() => setSignerKind('delegate')}
              className="mt-0.5"
            />
            Someone else will sign Purchase Order Terms on behalf of my company
          </label>

          {signerKind === 'delegate' && (
            <div className="grid gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
              <Field name="signerName" label="Signer name">
                <input id="signerName" name="signerName" required maxLength={200} className={inputClass} />
              </Field>
              <Field name="signerEmail" label="Signer email">
                <input
                  id="signerEmail"
                  name="signerEmail"
                  type="email"
                  required
                  maxLength={255}
                  className={inputClass}
                />
              </Field>
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--scw-gray-muted)]">
            Optional
          </legend>
          <Field name="accountsPayableEmail" label="Accounts payable email">
            <input
              id="accountsPayableEmail"
              name="accountsPayableEmail"
              type="email"
              maxLength={255}
              placeholder="Where invoices should be sent, if not you"
              className={inputClass}
            />
          </Field>

          <div>
            <span className={labelClass}>W-9</span>
            <label
              htmlFor="credit-terms-w9"
              className="mt-1 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-7 text-center transition-colors hover:border-[var(--scw-navy)]/40 hover:bg-gray-100"
            >
              <UploadCloud className="size-6 text-[var(--scw-gray-muted)]" />
              <span className="text-sm font-medium text-[var(--scw-navy)]">Click to upload</span>
              <span className="text-xs text-[var(--scw-gray-muted)]">
                PDF, up to {formatMb(MAX_DOCUMENT_BYTES)}. You can send it later if you do not have
                it to hand.
              </span>
              <input
                id="credit-terms-w9"
                name="file"
                type="file"
                accept="application/pdf"
                className="sr-only"
                onChange={(e) => {
                  pickFile(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            {file && (
              <div className="mt-1.5 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm">
                <FileText className="size-4 shrink-0 text-[var(--scw-gray-muted)]" />
                <span className="min-w-0 flex-1 truncate text-[var(--scw-gray-body)]">
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-[var(--scw-gray-muted)] transition-colors hover:text-red-600"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="size-4" />
                </button>
              </div>
            )}
          </div>

          <Field name="note" label="Anything else we should know">
            <textarea id="note" name="note" rows={3} maxLength={5000} className={inputClass} />
          </Field>
        </fieldset>

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="bg-[var(--scw-orange)] text-white hover:bg-[var(--scw-orange-hover)]"
        >
          {submitting ? 'Submitting…' : 'Submit application'}
        </Button>
      </form>
    </Shell>
  );
}
